/**
 * `worldFor(state)` — build the encounter's spatial **port**. This is the *per-encounter adapter
 * factory*: it knows the encounter's grid (and its extent) and returns the one `World<Cell>` the
 * engine binds. The engine (`reducer`, `spatialAnswerer`) holds only the returned `World<Cell>` —
 * it never branches on grid mode and never reads a cell's coordinates (`Cell` is opaque to it).
 * The map's extent (`inBounds`) is a *world property*: a hex disk measured by `mapRadius` (hex
 * modes), or a square rectangle `gridWidth × gridHeight` (square mode, read through
 * {@link gridExtent}). The two extents coexist by design — each mode binds the one that fits, the
 * other lies dormant.
 *
 * **Selection is by `gridMode`.** A `2d-square` encounter binds {@link squareWorldFor} (square
 * Chebyshev geometry, a `gridWidth × gridHeight` rectangle bound); the hex modes bind
 * {@link hexWorldFor} (hex geometry, a `mapRadius` disk bound). Both static impls are over the
 * bare `Hex`/`Square` coordinate; the engine binds the typed `World<Cell>` and the
 * {@link asWorldCell} port cast bridges — the engine never reads a cell's `grid` for geometry, it
 * picks the world by `gridMode`, and never sees this choice at all.
 */
import {
  type Hex,
  type Square,
  type World,
  axial,
  cellRegion,
  cone,
  distance as hexDistance,
  hexCone,
  hexKey,
  hexRay,
  hexSpace,
  makeWorld,
  ray,
  squareKey,
  squareSpace,
} from "@lancer-vtt/space";
import {
  type Cell,
  type EncounterState,
  encounterGridMode,
  gridExtent,
  tagCell,
} from "./types";

/** The centre of the hex-disk map bound. */
const HEX_ORIGIN: Hex = axial(0, 0);

/**
 * **The controlled port cast.** `Space<P>`/`World<P>` are generic
 * over the concrete coordinate; an encounter's cells are all the one grid's variant
 * of the tagged {@link Cell} union, so the engine binds `World<Cell>` and the static
 * impl (`hexSpace`/`squareSpace`, over `Hex`/`Square`) satisfies it structurally —
 * `key`/`distance`/`neighbors`/… read only the coordinate fields the variant carries.
 * The cast is sound *as port policy*: the engine never reads a cell's `grid` for
 * geometry (it picks the world by `state.gridMode`), and a world-geometry cell stays
 * within its own encounter's grid. The `grid` tag is real on driver-authored cells
 * (the wire/assertion surface); geometry cells are tag-free at runtime and that is
 * fine — see {@link Cell}.
 */
function asWorldCell<P>(w: World<P>): World<Cell> {
  return w as unknown as World<Cell>;
}

/**
 * The subset of an encounter that fully determines its world — the grid mode plus
 * the two extents. Narrowing {@link worldFor} to this (rather than the whole
 * `EncounterState`) costs existing callers nothing — a full state still satisfies it
 * — and lets a caller that has **no encounter at all** bind a world: the procedural
 * generator (`mapgen.ts`) needs one purely to key cells on a board it is inventing,
 * and would otherwise have to fabricate a fake `EncounterState` to ask for it.
 */
export type WorldSpec = Pick<EncounterState, "gridMode" | "gridWidth" | "gridHeight" | "mapRadius">;

export function worldFor(state: WorldSpec): World<Cell> {
  switch (encounterGridMode(state)) {
    case "2d-square":
      return asWorldCell(squareWorldFor(state));
    case "2d-hex":
    case "3d-hex":
    default:
      return hexWorldFor(state);
  }
}

/**
 * The hex spatial port: hex geometry with a hex-disk bound measured by `mapRadius`
 * from the origin. The live world for the hex grid modes (see {@link worldFor}).
 */
export function hexWorldFor(state: Pick<EncounterState, "mapRadius">): World<Cell> {
  return asWorldCell(
    makeWorld(hexSpace, (p) => hexDistance(HEX_ORIGIN, p) <= state.mapRadius, {
      // The hex twin of the square `ray`/`cone` the overlay reads, so the resolver's Line/Cone
      // stay grid-agnostic.
      linePattern: (origin, toward, len) => cellRegion(hexRay(origin, toward, len), hexKey),
      conePattern: (origin, toward, len) => cellRegion(hexCone(origin, toward, len), hexKey),
    }),
  );
}

/**
 * The square spatial port: square (Chebyshev) geometry with a rectangle bound
 * `0 ≤ x < gridWidth`, `0 ≤ y < gridHeight` (read through {@link gridExtent}). Reading `p.x`/`p.y`
 * here is an *adapter*-side coordinate read (the map knows its own shape) — never an engine read;
 * the engine only sees `World<P>`.
 */
export function squareWorldFor(
  // Narrowed to exactly what it reads (`gridExtent`'s own Pick) rather than the whole
  // state: the square world is fully determined by the board's extent, so a caller
  // that HAS no encounter — the procedural generator (`mapgen.ts`), sizing a space
  // purely to key cells — can build one without fabricating a fake `EncounterState`.
  state: Pick<EncounterState, "gridWidth" | "gridHeight" | "mapRadius">,
): World<Square> {
  const { width, height } = gridExtent(state);
  return makeWorld(
    squareSpace,
    (p) => p.x >= 0 && p.x < width && p.y >= 0 && p.y < height,
    {
      // Delegate to the SAME `ray`/`cone` primitives the overlay geometry (`attackRange.ts`)
      // uses, so the port and the overlay stay cell-for-cell identical on square.
      linePattern: (origin, toward, len) => cellRegion(ray(origin, toward, len), squareKey),
      conePattern: (origin, toward, len) => cellRegion(cone(origin, toward, len), squareKey),
    },
  );
}

/**
 * Parse a cell **key** (`"q,r"` for hex, `"x,y"` for square) into a *tagged*
 * {@link Cell} matching the encounter's grid — the mode-aware driver→domain parse
 * (the CLI/UI's `move`/`terrain`/`edge` cell args use this so a square encounter's
 * `move_token.to` is a self-describing `{grid:'square',x,y}` cell over the real ws
 * round-trip). Routes through the encounter's world (the right space's `parse`),
 * then stamps the grid family so the stored/wire cell is self-describing. Throws on
 * a malformed key (the space `parse` is the wall).
 */
export function parseCell(state: EncounterState, key: string): Cell {
  return tagCell(encounterGridMode(state), worldFor(state).parse(key));
}
